import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  FormControlLabel,
  Switch,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  HealthAndSafety as HealthIcon,
  Science as ScienceIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { mcpService, HealthStatus, type McpServerDto, type McpServerFilters } from '../services/mcpService';
import { useContextStore } from '../store/contextStore';
import { useNotificationStore } from '../store/notificationStore';
import { TextField } from '../@core/components/mui';
import DataTable, { type DataTableColumn } from '../components/data-display/DataTable';
import { PageListLayout } from '../components/layouts/PageListLayout';

const healthStatusColor = (status: string) => {
  switch (status) {
    case HealthStatus.Healthy:
      return 'success' as const;
    case HealthStatus.Unhealthy:
      return 'error' as const;
    case HealthStatus.Degraded:
      return 'warning' as const;
    default:
      return 'default' as const;
  }
};

/**
 * MCP servers list page component.
 */
export const McpServersListPage = () => {
  const navigate = useNavigate();
  const { addNotification } = useNotificationStore();
  const currentTenant = useContextStore((s) => s.currentTenant);
  const currentProject = useContextStore((s) => s.currentProject);

  const [servers, setServers] = useState<McpServerDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [discovering, setDiscovering] = useState<string | null>(null);
  const [checkingHealth, setCheckingHealth] = useState<string | null>(null);

  const [filters, setFilters] = useState<McpServerFilters>({
    pageNumber: 1,
    pageSize: 20,
    searchTerm: '',
    isActive: undefined,
  });

  const loadServers = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await mcpService.getMcpServers(filters);
      setServers(result.items);
      setTotalCount(result.totalCount);
    } catch (err: any) {
      setError(err.message || 'Failed to load MCP servers');
      addNotification('Failed to load MCP servers', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadServers();
  }, [filters.pageNumber, filters.pageSize, filters.isActive, filters.searchTerm, currentTenant?.id, currentProject?.id]);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this MCP server?')) {
      return;
    }

    try {
      await mcpService.deleteMcpServer(id);
      addNotification('MCP server deleted successfully', 'success');
      loadServers();
    } catch (err: any) {
      addNotification(err.message || 'Failed to delete MCP server', 'error');
    }
  };

  const handleDiscoverTools = async (id: string) => {
    setDiscovering(id);
    try {
      const result = await mcpService.discoverTools(id);
      addNotification(`Discovered ${result.toolsDiscovered} tools`, 'success');
      loadServers();
    } catch (err: any) {
      addNotification(err.message || 'Failed to discover tools', 'error');
    } finally {
      setDiscovering(null);
    }
  };

  const handleCheckHealth = async (id: string) => {
    setCheckingHealth(id);
    try {
      const result = await mcpService.checkHealth(id);
      if (result.isHealthy) {
        addNotification('Server is healthy', 'success');
      } else {
        addNotification(`Server health check failed: ${result.errorMessage || 'Unknown error'}`, 'warning');
      }
      loadServers();
    } catch (err: any) {
      addNotification(err.message || 'Failed to check health', 'error');
    } finally {
      setCheckingHealth(null);
    }
  };

  // Define table columns
  const tableColumns: DataTableColumn<McpServerDto>[] = [
    {
      id: 'name',
      label: 'Name',
      minWidth: 200,
      render: (server) => (
        <Box>
          <Typography
            variant="body2"
            fontWeight={500}
            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            onClick={() => navigate(`/mcp-servers/${server.id}`)}
          >
            {server.name}
          </Typography>
          {server.description && (
            <Typography variant="caption" color="text.secondary">
              {server.description}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      id: 'serverUrl',
      label: 'Server URL',
      minWidth: 250,
      render: (server) => (
        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
          {server.serverUrl}
        </Typography>
      ),
    },
    {
      id: 'healthStatus',
      label: 'Health Status',
      minWidth: 130,
      render: (server) => (
        <Chip label={server.healthStatus} color={healthStatusColor(server.healthStatus)} size="small" />
      ),
    },
    {
      id: 'lastHealthCheckAt',
      label: 'Last Health Check',
      minWidth: 180,
      render: (server) =>
        server.lastHealthCheckAt ? new Date(server.lastHealthCheckAt).toLocaleString() : 'Never',
    },
    {
      id: 'toolCount',
      label: 'Tools',
      minWidth: 80,
      align: 'right',
    },
    {
      id: 'isActive',
      label: 'Active',
      minWidth: 100,
      render: (server) => (
        <Chip
          label={server.isActive ? 'Active' : 'Inactive'}
          color={server.isActive ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 200,
      align: 'right',
      render: (server) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <Tooltip title="Discover Tools">
            <IconButton
              size="small"
              onClick={() => handleDiscoverTools(server.id)}
              disabled={discovering === server.id}
            >
              {discovering === server.id ? (
                <CircularProgress size={20} />
              ) : (
                <ScienceIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Check Health">
            <IconButton
              size="small"
              onClick={() => handleCheckHealth(server.id)}
              disabled={checkingHealth === server.id}
            >
              {checkingHealth === server.id ? (
                <CircularProgress size={20} />
              ) : (
                <HealthIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => navigate(`/mcp-servers/${server.id}/edit`)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => handleDelete(server.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const totalPages = Math.ceil(totalCount / (filters.pageSize || 20));

  return (
    <PageListLayout
      title="MCP Servers"
      createButton={{
        label: 'Create MCP Server',
        onClick: () => navigate('/mcp-servers/new'),
        icon: <AddIcon />,
      }}
      filters={
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label="Search MCP servers"
            size="small"
            value={filters.searchTerm || ''}
            onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value, pageNumber: 1 })}
            sx={{ flexGrow: 1, minWidth: 200 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={filters.isActive === true}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    isActive: e.target.checked ? true : undefined,
                    pageNumber: 1,
                  })
                }
              />
            }
            label="Active Only"
          />
        </Box>
      }
      loading={loading}
      error={error || undefined}
      onErrorDismiss={() => setError(null)}
      isEmpty={!loading && servers.length === 0}
      emptyState={{
        title: 'No MCP servers found',
        description: 'Create a new MCP server to get started.',
        action: {
          label: 'Create MCP Server',
          onClick: () => navigate('/mcp-servers/new'),
        },
      }}
      pagination={{
        page: filters.pageNumber || 1,
        totalPages,
        onChange: (page) => setFilters({ ...filters, pageNumber: page }),
      }}
    >
      <DataTable
        columns={tableColumns}
        rows={servers}
      />
    </PageListLayout>
  );
};
